import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Download, Search, RefreshCw, ShoppingBag, CheckCircle2,
  Clock, XCircle, DollarSign, User, Calendar, Package,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/orders")({
  component: OrdersPage,
});

type OrderStatus = "pending" | "paid" | "fulfilled" | "refunded" | "cancelled";

type Order = {
  id: string;
  user_id: string | null;
  guest_email: string | null;
  guest_name: string | null;
  status: OrderStatus;
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  affiliate_code: string | null;
};

const STATUS_META: Record<OrderStatus, { label: string; className: string }> = {
  pending: { label: "Pending", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  paid: { label: "Paid", className: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  fulfilled: { label: "Fulfilled", className: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  refunded: { label: "Refunded", className: "bg-rose-500/15 text-rose-600 border-rose-500/30" },
  cancelled: { label: "Cancelled", className: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30" },
};

const STATUS_FILTERS: (OrderStatus | "all")[] = ["all", "pending", "paid", "fulfilled", "refunded", "cancelled"];

function customerLabel(o: Order) {
  return o.guest_name || o.guest_email || (o.user_id ? `user:${o.user_id.slice(0, 8)}` : "Guest");
}

function OrdersPage() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<OrderStatus | "all">("all");
  const [refreshing, setRefreshing] = useState(false);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin_orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data as Order[];
    },
  });

  const stats = useMemo(() => {
    const paid = orders.filter((o) => o.status === "paid" || o.status === "fulfilled");
    const pending = orders.filter((o) => o.status === "pending");
    const refunded = orders.filter((o) => o.status === "refunded");
    const revenue = paid.reduce((s, o) => s + o.total_cents, 0) / 100;
    return {
      total: orders.length,
      revenue,
      paid: paid.length,
      pending: pending.length,
      refunded: refunded.length,
    };
  }, [orders]);

  const counts = useMemo(() => {
    const m: Record<string, number> = { all: orders.length };
    for (const s of Object.keys(STATUS_META)) m[s] = 0;
    for (const o of orders) m[o.status] = (m[o.status] ?? 0) + 1;
    return m;
  }, [orders]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (!q) return true;
      return (
        customerLabel(o).toLowerCase().includes(q) ||
        (o.guest_email ?? "").toLowerCase().includes(q) ||
        (o.affiliate_code ?? "").toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q)
      );
    });
  }, [orders, query, status]);

  const setOrderStatus = async (id: string, s: OrderStatus) => {
    const patch: any = { status: s };
    if (s === "paid") patch.paid_at = new Date().toISOString();
    const { error } = await supabase.from("orders").update(patch).eq("id", id);
    if (error) toast.error(error.message);
    else {
      qc.invalidateQueries({ queryKey: ["admin_orders"] });
      qc.invalidateQueries({ queryKey: ["order_detail", id] });
      toast.success(`Order marked ${s}`);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await qc.invalidateQueries({ queryKey: ["admin_orders"] });
    setRefreshing(false);
    toast.success("Orders refreshed");
  };

  const exportCsv = () => {
    const rows = [
      ["ID", "Date", "Customer", "Email", "Status", "Total", "Currency", "Ref"],
      ...filtered.map((o) => [
        o.id,
        new Date(o.created_at).toISOString(),
        customerLabel(o),
        o.guest_email ?? "",
        o.status,
        (o.total_cents / 100).toFixed(2),
        o.currency,
        o.affiliate_code ?? "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Orders"
        description="All customer purchases across every commerce module."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="gap-1.5">
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1.5">
              <Download className="h-4 w-4" /> CSV
            </Button>
          </div>
        }
      />

      <StatsStrip
        tiles={[
          { label: "Total orders", value: stats.total, icon: ShoppingBag, tone: "default" },
          { label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, icon: DollarSign, tone: "success", hint: "Paid + fulfilled" },
          { label: "Paid", value: stats.paid, icon: CheckCircle2, tone: "info" },
          { label: "Pending", value: stats.pending, icon: Clock, tone: "warning" },
          { label: "Refunded", value: stats.refunded, icon: XCircle, tone: "danger" },
        ]}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer, email, ref, or order id…"
            className="pl-9"
          />
        </div>
        <div className="-mx-1 flex flex-nowrap gap-1.5 overflow-x-auto px-1 sm:flex-wrap sm:overflow-visible">
          {STATUS_FILTERS.map((s) => {
            const active = status === s;
            const label = s === "all" ? "All" : STATUS_META[s].label;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
                <span className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums",
                  active ? "bg-primary-foreground/20" : "bg-muted",
                )}>
                  {counts[s] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5 text-right">Total</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Ref</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Loading orders…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  {orders.length === 0 ? "No orders yet." : "No orders match your filters."}
                </td></tr>
              )}
              {filtered.map((o) => {
                const meta = STATUS_META[o.status];
                return (
                  <tr key={o.id} className="border-t border-border hover:bg-secondary/40">
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      <div>{new Date(o.created_at).toLocaleDateString()}</div>
                      <div className="text-[11px]">{new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{customerLabel(o)}</div>
                      {o.guest_email && o.guest_name && (
                        <div className="text-xs text-muted-foreground">{o.guest_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                      ${(o.total_cents / 100).toFixed(2)} <span className="text-xs text-muted-foreground">{o.currency}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={cn("border", meta.className)}>{meta.label}</Badge>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{o.affiliate_code ?? "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpenId(o.id)}>View</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="grid gap-3 md:hidden">
        {isLoading && (
          <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">Loading orders…</div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
            {orders.length === 0 ? "No orders yet." : "No orders match your filters."}
          </div>
        )}
        {filtered.map((o) => {
          const meta = STATUS_META[o.status];
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setOpenId(o.id)}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition hover:border-primary/40"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 shrink-0" />
                  <span>{new Date(o.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5 truncate">
                  <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{customerLabel(o)}</span>
                </div>
                {o.affiliate_code && (
                  <div className="text-[11px] font-mono text-muted-foreground">ref: {o.affiliate_code}</div>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <div className="font-bold tabular-nums">${(o.total_cents / 100).toFixed(2)}</div>
                <Badge variant="outline" className={cn("border text-[10px]", meta.className)}>{meta.label}</Badge>
              </div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-4 w-4" /> Order detail
            </DialogTitle>
          </DialogHeader>
          {openId && <OrderDetail orderId={openId} onStatusChange={setOrderStatus} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrderDetail({ orderId, onStatusChange }: { orderId: string; onStatusChange: (id: string, s: OrderStatus) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["order_detail", orderId],
    queryFn: async () => {
      const [{ data: order }, { data: items }] = await Promise.all([
        supabase.from("orders").select("*").eq("id", orderId).single(),
        supabase.from("order_items").select("*").eq("order_id", orderId),
      ]);
      return { order, items: items ?? [] };
    },
  });
  if (isLoading || !data?.order) return <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>;
  const o = data.order;
  const meta = STATUS_META[o.status as OrderStatus];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 p-3">
        <div>
          <div className="text-xs text-muted-foreground">Order ID</div>
          <div className="font-mono text-xs">{o.id}</div>
        </div>
        <Badge variant="outline" className={cn("border", meta.className)}>{meta.label}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Stat label="Customer" value={o.guest_name ?? o.guest_email ?? (o.user_id ? o.user_id.slice(0, 8) : "—")} />
        <Stat label="Subtotal" value={`$${(o.subtotal_cents / 100).toFixed(2)}`} />
        <Stat label="Discount" value={`$${(o.discount_cents / 100).toFixed(2)}`} />
        <Stat label="Total" value={`$${(o.total_cents / 100).toFixed(2)}`} strong />
      </div>

      <div>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Items</div>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <tbody>
              {data.items.length === 0 && (
                <tr><td className="px-3 py-3 text-center text-muted-foreground">No line items</td></tr>
              )}
              {data.items.map((i: any) => (
                <tr key={i.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">{i.product_name}</div>
                    {i.variant_name && <div className="text-xs text-muted-foreground">{i.variant_name}</div>}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground">× {i.qty}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">${((i.unit_price_cents * i.qty) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Change status:</span>
          <Select value={o.status} onValueChange={(v) => onStatusChange(o.id, v as OrderStatus)}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="fulfilled">Fulfilled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <Link to="/receipt/$orderId" params={{ orderId: o.id }} target="_blank">
            <Download className="h-3.5 w-3.5" /> Download receipt
          </Link>
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn("truncate", strong ? "font-bold text-base tabular-nums" : "text-sm")}>{value}</div>
    </div>
  );
}
